import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import { GroupChannel, GroupChannelHandler } from "@sendbird/chat/groupChannel";
import { ConnectionHandler } from "@sendbird/chat";
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { useQueryClient } from "@tanstack/react-query";
import { useMessageNotificationSound } from "./use-message-notification-sound";
import { isNotificationMuteActive } from "./use-notification-mute-store";
import { isSupportChatMuted } from "./use-support-chat-mute-store";
import { useSupportChatFavoritesStore } from "./use-support-chat-favorites-store";
import { useChatWorkspaceAccess } from "./use-chat-workspace-access";
import { useNewMentorUI } from "@/lib/hooks/use-new-mentor-ui";
import { INTERNAL_OPS_CUSTOM_TYPES } from "@/lib/constants";
import { useTrackEvent, PostHogEvents } from "@/lib/posthog-events";

interface ChannelsListData {
  channels: GroupChannel[];
  loading: boolean;
  error: string | null;
}

const ChatChannelsContext = createContext<ChannelsListData | null>(null);

export function buildSharedCustomTypes({
  canSeeTeens,
  canSeeSupport,
  isNewMentorUI,
}: {
  canSeeTeens: boolean;
  canSeeSupport: boolean;
  isNewMentorUI: boolean;
}): string[] {
  const types: string[] = [];
  if (canSeeTeens) {
    types.push("MENTORSHIP");
    if (!isNewMentorUI) types.push("SUPERVISION");
  }
  if (canSeeSupport) types.push(...INTERNAL_OPS_CUSTOM_TYPES);
  return types;
}

export function filterSharedChannels(
  channels: GroupChannel[],
  customTypesFilter: string[],
): GroupChannel[] {
  if (customTypesFilter.length === 0) return channels;
  return channels.filter((channel) =>
    customTypesFilter.includes(channel.customType ?? ""),
  );
}

interface UseChannelsListProps {
  customTypesFilter?: string[];
  enabled?: boolean;
  /**
   * Controls sound + analytics side effects on `onMessageReceived`. Defaults
   * to true so callers that own the conversations UI keep firing. Background
   * subscribers (e.g. the global sidebar badge) should pass `false` to avoid
   * double-firing alongside the conversations page.
   */
  enableNotifications?: boolean;
}

function useChannelsListSubscription({
  customTypesFilter = [],
  enabled = true,
  enableNotifications = true,
}: UseChannelsListProps = {}) {
  const [channels, setChannels] = useState<GroupChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lastVisibilityChange = useRef<number>(Date.now());
  const isReconnecting = useRef<boolean>(false);
  const syncChannelsRef = useRef<() => Promise<void>>();

  const store = useSendbirdStateContext();
  const sdkStore = store?.stores?.sdkStore;
  const sdk = enabled ? sdkStore?.sdk : null;
  const { playTeenMessageSound, playOpsMessageSound } =
    useMessageNotificationSound();
  const { track } = useTrackEvent();
  const trackRef = useRef(track);
  trackRef.current = track;
  const notificationsEnabledRef = useRef(enableNotifications);
  notificationsEnabledRef.current = enableNotifications;
  const customTypesFilterKey = JSON.stringify(customTypesFilter);
  const stableCustomTypesFilter = useMemo<string[]>(
    () => JSON.parse(customTypesFilterKey),
    [customTypesFilterKey],
  );

  // Invalidate mentorship list when a new Sendbird channel appears so BE-side
  // data (e.g. magic moment Day 1 state) is refetched without a manual reload.
  const queryClient = useQueryClient();
  const invalidateMentorshipsOnNewChannel = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["mentorship"] });
  }, [queryClient]);

  const fetchChannels = useCallback(async () => {
    if (!sdk) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const query = await sdk.groupChannel?.createMyGroupChannelListQuery({
        order: "latest_last_message",
        includeFrozen: false,
        includeEmpty: true,
        customTypesFilter: stableCustomTypesFilter,
        limit: 100,
      });

      const allChannels: GroupChannel[] = [];
      if (query) {
        while (query.hasNext) {
          const page = await query.next();
          if (!page || page.length === 0) break;
          allChannels.push(...page);
        }
      }

      allChannels.sort((a, b) => {
        const aTime = a.lastMessage?.createdAt || 0;
        const bTime = b.lastMessage?.createdAt || 0;
        return bTime - aTime;
      });

      setChannels(allChannels);
    } catch (err) {
      console.error("Error fetching channels:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch channels");
    } finally {
      setLoading(false);
    }
  }, [sdk, stableCustomTypesFilter]);

  const syncChannelsAfterReconnection = useCallback(async () => {
    if (!sdk || isReconnecting.current) return;

    // Check connection state before attempting sync
    const connectionState = sdk.connectionState;
    if (connectionState !== "OPEN") {
      console.log(
        `Connection not ready (state: ${connectionState}), skipping sync`,
      );
      return;
    }

    isReconnecting.current = true;
    console.log("Syncing channels after reconnection...");

    try {
      // Clear any previous connection errors
      setError(null);
      await fetchChannels();
      console.log("Channel sync completed after reconnection");
    } catch (error) {
      console.error("Failed to sync channels after reconnection:", error);
      setError("Failed to sync messages. Please refresh the page.");
    } finally {
      isReconnecting.current = false;
    }
  }, [fetchChannels, sdk]);

  // Store the sync function in a ref to avoid dependency issues
  syncChannelsRef.current = syncChannelsAfterReconnection;

  useEffect(() => {
    if (!sdk) {
      setLoading(false);
      return;
    }

    // Generate unique handler IDs
    const channelHandlerId = `channel-list-${Date.now()}-${Math.random()}`;
    const connectionHandlerId = `connection-${Date.now()}-${Math.random()}`;

    // Use timeout to ensure SDK is fully initialized (similar to _authenticated.tsx pattern)
    const timeout = setTimeout(async () => {
      try {
        // Connection handler for managing reconnection
        const connectionHandler = new ConnectionHandler({
          onReconnectStarted: () => {
            console.log("Reconnection started...");
            setError(null); // Clear previous errors during reconnection attempt
          },
          onReconnectSucceeded: async () => {
            console.log("Reconnection succeeded, syncing channels...");
            setError(null);
            // Add small delay to ensure connection is stable
            setTimeout(async () => {
              await syncChannelsRef.current?.();
            }, 1000);
          },
          onReconnectFailed: () => {
            console.log("Reconnection failed");
            setError(
              "Connection lost. Please check your network and refresh the page.",
            );
          },
        });

        const matchesFilter = (groupChannel: GroupChannel) =>
          stableCustomTypesFilter.length === 0 ||
          stableCustomTypesFilter.includes(groupChannel.customType ?? "");

        const channelHandler = new GroupChannelHandler({
          onMessageReceived: (channel, message) => {
            const groupChannel = channel as GroupChannel;

            if (!matchesFilter(groupChannel)) return;

            if (notificationsEnabledRef.current) {
              const senderId = (message as any)?.sender?.userId;
              const isFromSelf =
                senderId && senderId === sdk.currentUser?.userId;

              if (!isFromSelf) {
                const muted = isNotificationMuteActive();
                if (groupChannel.customType === "MENTORSHIP") {
                  if (!muted) playTeenMessageSound();

                  if (senderId) {
                    trackRef.current(PostHogEvents.TEEN_MESSAGE_RECEIVED, {
                      teen_id: senderId,
                      channel_url: groupChannel.url,
                      message_type: (message as any)?.customType || "text",
                    });
                  }
                } else if (
                  (INTERNAL_OPS_CUSTOM_TYPES as readonly string[]).includes(
                    groupChannel.customType ?? "",
                  )
                ) {
                  const supportMuted = isSupportChatMuted(
                    sdk.currentUser?.userId,
                  );
                  if (!muted && !supportMuted) playOpsMessageSound();
                }
              }
            }

            setChannels((prevChannels) => {
              const existingIndex = prevChannels.findIndex(
                (ch) => ch.url === groupChannel.url,
              );
              let nextChannels: GroupChannel[];
              if (existingIndex === -1) {
                nextChannels = [groupChannel, ...prevChannels];
                invalidateMentorshipsOnNewChannel();
              } else {
                nextChannels = prevChannels.map((ch) =>
                  ch.url === groupChannel.url ? groupChannel : ch,
                );
              }
              return nextChannels.sort((a, b) => {
                const aTime = a.lastMessage?.createdAt || 0;
                const bTime = b.lastMessage?.createdAt || 0;
                return bTime - aTime;
              });
            });
          },
          onChannelChanged: (channel) => {
            const groupChannel = channel as GroupChannel;
            if (!matchesFilter(groupChannel)) return;
            setChannels((prevChannels) => {
              const exists = prevChannels.some(
                (ch) => ch.url === groupChannel.url,
              );
              if (!exists) invalidateMentorshipsOnNewChannel();
              const updated = exists
                ? prevChannels.map((ch) =>
                    ch.url === groupChannel.url ? groupChannel : ch,
                  )
                : [groupChannel, ...prevChannels];
              return updated;
            });
          },
          onChannelDeleted: (channelUrl) => {
            // A deleted channel is confirmed gone, so drop any stale favorite
            // entry. Frozen channels never fire this, so they stay favorited.
            useSupportChatFavoritesStore.getState().prune(String(channelUrl));
            setChannels((prevChannels) =>
              prevChannels.filter((ch) => ch.url !== channelUrl),
            );
          },
        });

        // Add the connection handler
        if (typeof sdk?.addConnectionHandler === "function") {
          sdk.addConnectionHandler(connectionHandlerId, connectionHandler);
        } else {
          console.error("Failed to add connectionHandler - SDK not ready");
        }

        // Add the channel handler
        if (typeof sdk?.groupChannel?.addGroupChannelHandler === "function") {
          sdk.groupChannel.addGroupChannelHandler(
            channelHandlerId,
            channelHandler,
          );
        } else {
          console.error("Failed to add groupChannelHandler - SDK not ready");
        }

        // Initial fetch
        await fetchChannels();
      } catch (error) {
        console.error("Error setting up channel list handlers:", error);
      }
    }, 100); // Wait 100ms for SDK to be ready

    // Cleanup
    return () => {
      clearTimeout(timeout);
      try {
        if (typeof sdk?.removeConnectionHandler === "function") {
          sdk.removeConnectionHandler(connectionHandlerId);
        }
        if (
          typeof sdk?.groupChannel?.removeGroupChannelHandler === "function"
        ) {
          sdk.groupChannel.removeGroupChannelHandler(channelHandlerId);
        }
      } catch (error) {
        console.error("Failed to remove handlers:", error);
      }
    };
  }, [
    sdk,
    stableCustomTypesFilter,
    playTeenMessageSound,
    playOpsMessageSound,
    invalidateMentorshipsOnNewChannel,
    fetchChannels,
  ]);

  // Page visibility API integration for handling background/foreground transitions
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden && sdk) {
        const timeSinceLastChange = Date.now() - lastVisibilityChange.current;
        // If the page was hidden for more than 30 seconds, sync channels
        if (timeSinceLastChange > 30000) {
          console.log(
            `Page visible after ${timeSinceLastChange}ms, syncing channels...`,
          );
          await syncChannelsRef.current?.();
        }
      }
      lastVisibilityChange.current = Date.now();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [sdk]);

  // On a cold/hard refresh the Sendbird SDK connection isn't established yet, so
  // the channel query reports loading=false with empty data before anything has
  // actually loaded. Treat that initial connection window as loading so
  // consumers render a skeleton instead of flashing the empty state. An SDK
  // error opts out so a failed connection doesn't keep the skeleton up forever.
  const sdkConnecting = enabled && !sdkStore?.initialized && !sdkStore?.error;

  return {
    channels,
    loading: loading || sdkConnecting,
    error,
  };
}

export function ChatChannelsProvider({ children }: PropsWithChildren) {
  const { canSeeTeens, canSeeSupport } = useChatWorkspaceAccess();
  const isNewMentorUI = useNewMentorUI();
  const customTypesFilter = useMemo(
    () => buildSharedCustomTypes({ canSeeTeens, canSeeSupport, isNewMentorUI }),
    [canSeeTeens, canSeeSupport, isNewMentorUI],
  );
  const { channels, loading, error } = useChannelsListSubscription({
    customTypesFilter,
    enabled: customTypesFilter.length > 0,
  });
  const value = useMemo(
    () => ({ channels, loading, error }),
    [channels, loading, error],
  );

  return createElement(ChatChannelsContext.Provider, { value }, children);
}

export function useChannelsList({
  customTypesFilter = [],
  enabled = true,
  enableNotifications = true,
}: UseChannelsListProps = {}): ChannelsListData {
  const shared = useContext(ChatChannelsContext);
  const local = useChannelsListSubscription({
    customTypesFilter,
    enabled: enabled && shared === null,
    enableNotifications,
  });

  if (!enabled) return { channels: [], loading: false, error: null };
  if (!shared) return local;

  return {
    channels: filterSharedChannels(shared.channels, customTypesFilter),
    loading: shared.loading,
    error: shared.error,
  };
}
